import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    TagGroup,
    Tag,
    InteractionTag,
    TagGroupProps,
    InteractionTagPrimary,
    InteractionTagSecondary,
    Button,
    makeStyles,
    Overflow,
    OverflowItem,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    useIsOverflowItemVisible,
    useOverflowMenu,
    tokens,
    Avatar,
    Tooltip,
} from '@fluentui/react-components';
import { clearContext, removeFromContext, RootState } from '../../utils/store';

const useMenuItemStyles = makeStyles({
    menuItem: {
        padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalXS}`,
        ':hover': {
            color: tokens.colorNeutralForeground2Hover,
        },
    },
    tag: {
        backgroundColor: 'transparent',
        //borderColor: 'transparent',
    },
});

const OverflowMenuItem = ({
    tag,
    onRemove,
}: {
    tag: { value: string; children: React.ReactNode };
    onRemove: (event: React.MouseEvent, data: { value: string }) => void;
}) => {
    const isVisible = useIsOverflowItemVisible(tag.value!);
    const styles = useMenuItemStyles();
    if (isVisible) {
        return null;
    }
    return (
        <MenuItem key={tag.value} className={styles.menuItem}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <InteractionTagPrimary {...tag} className={styles.tag} />
                <InteractionTagSecondary
                    aria-label="remove"
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent the menu from closing
                        onRemove(e, { value: tag.value });
                    }}
                />
            </div>
        </MenuItem>
    );
};

const OverflowMenu = ({
    items,
    onRemove,
}: {
    items: { value: string; children: React.ReactNode }[];
    onRemove: (event: React.MouseEvent, data: { value: string }) => void;
}) => {
    const { ref, isOverflowing, overflowCount } =
        useOverflowMenu<HTMLButtonElement>();
    if (!isOverflowing) {
        return null;
    }
    return (
        <InteractionTag>
            <Menu>
                <MenuTrigger disableButtonEnhancement>
                    <InteractionTagPrimary
                        ref={ref}
                        aria-label={`${overflowCount} more tags`}
                    >
                        {`+${overflowCount}`}
                    </InteractionTagPrimary>
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        {items.map((item) => (
                            <OverflowMenuItem
                                key={item.value}
                                tag={item}
                                onRemove={onRemove}
                            />
                        ))}
                    </MenuList>
                </MenuPopover>
            </Menu>
        </InteractionTag>
    );
};

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '10px',
        width: '70%',
    },
    resetButton: {
        width: 'fit-content',
    },
    tagGroup: {
        display: 'flex',
        overflow: 'hidden',
        padding: '5px',
        zIndex: 0,
        height: 'fit-content',
        minWidth: '150px',
        //resize: 'horizontal',
        width: '100%',
        boxSizing: 'border-box',
    },
});

/**
 * focus management for the reset button
 */
const useResetExample = (visibleTagsLength: number) => {
    const resetButtonRef = React.useRef<HTMLButtonElement>(null);
    const firstTagRef = React.useRef<HTMLButtonElement>(null);

    const prevVisibleTagsLengthRef = React.useRef<number>(visibleTagsLength);
    React.useEffect(() => {
        if (visibleTagsLength === 0) {
            resetButtonRef.current?.focus();
        } else if (prevVisibleTagsLengthRef.current === 0) {
            firstTagRef.current?.focus();
        }

        prevVisibleTagsLengthRef.current = visibleTagsLength;
    }, [visibleTagsLength]);

    return { firstTagRef, resetButtonRef };
};

const DismissWithInteractionTags = () => {
    //const [visibleTags, setVisibleTags] = React.useState(initialTags);
    const visibleTags = useSelector(
        (state: RootState) => state.settings.context
    );
    const dispatch = useDispatch();
    const removeItem: TagGroupProps['onDismiss'] = (_e, { value }) => {
        //setVisibleTags([...visibleTags].filter((tag) => tag.value !== value));
        dispatch(removeFromContext(value));
    };
    const resetItems = () => clearContext();
    const { firstTagRef, resetButtonRef } = useResetExample(
        Object.keys(visibleTags).length
    );

    const styles = useStyles();

    const tagItems = Object.entries(visibleTags).map(
        ([key, searchResults]) => ({
            key: searchResults.Value,
            value: key,
            children: searchResults.DisplayName,
            type: searchResults.ContextType,
        })
    );

    return (
        <>
            {Object.keys(visibleTags).length !== 0 && (
                <Overflow minimumVisible={2} padding={60}>
                    <TagGroup
                        onDismiss={removeItem}
                        aria-label="Dismiss example"
                        className={styles.tagGroup}
                    >
                        {tagItems.map(({ value, type, ...rest }) => (
                            <OverflowItem key={rest.key} id={value!}>
                                <InteractionTag key={value} value={value}>
                                    <Tooltip
                                        content={type}
                                        relationship="label"
                                    >
                                        <InteractionTagPrimary {...rest} />
                                    </Tooltip>
                                    <InteractionTagSecondary aria-label="remove" />
                                </InteractionTag>
                            </OverflowItem>
                        ))}
                        <OverflowMenu items={tagItems} onRemove={removeItem} />
                    </TagGroup>
                </Overflow>
            )}
        </>
    );
};

interface ContextTagsProps {
    initialTags: { value: string; children: React.ReactNode }[];
    onTagRemove: (tagValue: string) => void;
}

export const ContextTags = () => {
    const styles = useStyles();
    return (
        <div className={styles.container}>
            <DismissWithInteractionTags />
        </div>
    );
};

export default ContextTags;
